import { inject, Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { EpaycoLoaderService } from '../../shared/services/epayco-loader.service';
import { MembershipService as SharedMembershipService, BusinessMembershipDto } from '../../shared/services/membership.service';
import { MembershipService } from './membership.service';
import { AuthService } from '../auth';
import { IpService } from '../../shared/services';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ProfessionalPaymentService {
  private epaycoLoader = inject(EpaycoLoaderService);
  private sharedMembershipService = inject(SharedMembershipService);
  private membershipService = inject(MembershipService);
  private authService = inject(AuthService);
  private ipService = inject(IpService);

  /**
   * Purchase additional professionals with full period pricing
   * Opens Epayco checkout popup
   * @param quantity Number of professionals to purchase (default: 1)
   * @returns Observable<void> - Resolves when popup is opened
   */
  purchaseProfessionals(quantity: number = 1): Observable<void> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not available');
    }

    // Get user info
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) {
      throw new Error('User info not available');
    }

    const { email, given_name, family_name, phone } = userInfo as any;

    // Get membership data and calculate full period price
    return forkJoin({
      membership: this.sharedMembershipService.getLatestBusinessMembershipFresh(businessId),
      daysRemaining: this.membershipService.getDaysUntilRenewal(),
      pricePerProfessional: this.membershipService.getPricePerProfessional(),
      clientIp: this.ipService.getPublicIp(),
      plans: this.sharedMembershipService.getPlans(),
    }).pipe(
      switchMap(({ membership, daysRemaining, pricePerProfessional, clientIp, plans }) => {
        if (!membership) {
          throw new Error('Membership not found');
        }

        // Find the plan with features from plans list
        const planWithFeatures = plans.membershipPlans.find(
          (p: any) => p.membershipPlanId === membership.membershipPlanId
        );

        // Find "Múltiples profesionales" feature
        const professionalFeature = planWithFeatures?.features?.find(
          (f: any) => f.featureName === 'Múltiples profesionales'
        );

        if (!professionalFeature) {
          throw new Error('Professional feature not found in membership plan');
        }

        // Calculate full period price for all professionals
        const pricePerOne = this.membershipService.calculateProportionalProfessionalPrice(
          daysRemaining,
          pricePerProfessional
        );
        const fullPrice = pricePerOne * quantity;

        // Build features array for extra4
        const featuresToSend = [
          {
            membershipPlanFeatureId: professionalFeature.membershipPlanFeatureId,
            quantity: quantity,
            featureName: 'Múltiples profesionales',
          },
        ];

        const featuresJson = JSON.stringify(featuresToSend);
        const fullName = `${given_name} ${family_name}`.trim();
        const invoice = `PROF-${Date.now()}`;
        const responseUrl = `${window.location.origin}/professionals`;
        const webhookUrl = environment.epaycoWebhookUrl;

        // Open Epayco checkout
        return this.openEpaycoCheckout({
          amount: fullPrice,
          quantity,
          featuresJson,
          invoice,
          responseUrl,
          webhookUrl,
          fullName,
          email,
          phone: phone || '',
          clientIp,
          businessId,
        });
      }),
      catchError((error) => {
        console.error('Error purchasing professional:', error);
        throw error;
      })
    );
  }

  /**
   * Open Epayco checkout popup
   */
  private openEpaycoCheckout(config: {
    amount: number;
    quantity: number;
    featuresJson: string;
    invoice: string;
    responseUrl: string;
    webhookUrl: string;
    fullName: string;
    email: string;
    phone: string;
    clientIp: string;
    businessId: string;
  }): Observable<void> {
    return new Observable<void>((observer) => {
      this.epaycoLoader.load().then((ePayco) => {
        const handler = ePayco.checkout.configure({
          key: environment.epaycoPublicKey,
          test: environment.epaycoTestMode,
        });

        const [firstName, ...lastNameParts] = config.fullName.split(' ');
        const lastName = lastNameParts.join(' ') || firstName;

        handler.open({
          // Basic checkout data - Single payment (not subscription)
          name: config.quantity > 1 ? `${config.quantity} Profesionales adicionales` : 'Profesional adicional',
          description: config.quantity > 1
            ? `${config.quantity} profesionales adicionales - ${config.amount.toLocaleString('es-CO')} COP`
            : `Profesional adicional - ${config.amount.toLocaleString('es-CO')} COP`,
          currency: 'COP',
          amount: config.amount,
          country: 'CO',
          response: config.responseUrl,
          confirmation: config.webhookUrl,
          test: environment.epaycoTestMode,
          // Invoice for single payment
          invoice: config.invoice,
          // Metadata
          extra1: config.businessId,
          extra2: config.fullName,
          extra3: config.email,
          extra4: config.featuresJson, // Features: 1 additional professional
          // Payer data
          payerFirstName: firstName,
          payerLastName: lastName,
          payerEmail: config.email,
          payerPhone: config.phone,
          clientIp: config.clientIp,
          name_billing: config.fullName,
          email_billing: config.email,
          mobilephone_billing: config.phone || undefined,
          testMode: environment.epaycoTestMode,
        });

        // Resolve immediately after opening popup
        observer.next();
        observer.complete();
      }).catch((error) => {
        observer.error(error);
      });
    });
  }

  /**
   * Get business ID from JWT token (same approach as MembershipService)
   */
  private getBusinessId(): string | null {
    try {
      const authData = localStorage.getItem('auth_data');
      if (!authData) {
        return null;
      }

      const parsed = JSON.parse(authData);
      if (!parsed.token) {
        return null;
      }

      const parts = parsed.token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      return payload.business_id || null;
    } catch (error) {
      return null;
    }
  }
}

